import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../Providers/AuthProvider';

export const OperatorsManager = () => {
  const { authenticatedFetch, user } = useAuth();
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Form para crear operador
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('Operador');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form para cambio de contraseña propia
  const [currentPassword, setCurrentPassword] = useState('');
  const [myNewPassword, setMyNewPassword] = useState('');
  const [isChangingPass, setIsChangingPass] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

  const fetchOperators = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await authenticatedFetch(`${API_URL}v2/auth/operators`);
      if (res.ok) {
        const data = await res.json();
        setOperators(data);
      } else {
        setError('No se pudieron cargar los operadores');
      }
    } catch (err) {
      console.error(err);
      setError('Error de conexión al cargar operadores');
    } finally {
      setLoading(false);
    }
  }, [API_URL, authenticatedFetch]);

  useEffect(() => {
    if (user?.role === 'BusinessAdmin' || user?.role === 'SuperAdmin') {
      fetchOperators();
    } else {
      setLoading(false);
    }
  }, [user, fetchOperators]);

  const handleCreateOperator = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!newUsername.trim() || !newPassword.trim()) {
      setError('Escribe el usuario y la contraseña.');
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await authenticatedFetch(`${API_URL}v2/auth/register-operator`, {
        method: 'POST',
        body: JSON.stringify({
          username: newUsername.trim(),
          password: newPassword,
          role: newRole
        })
      });

      const data = await res.json();
      if (res.ok) {
        setSuccessMsg('Operador creado exitosamente.');
        setNewUsername('');
        setNewPassword('');
        setNewRole('Operador');
        fetchOperators();
      } else {
        setError(data.message || 'Error al crear operador.');
      }
    } catch (err) {
      setError('Error de conexión con el servidor.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeOwnPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!currentPassword || !myNewPassword) {
      setError('Completa ambos campos de contraseña.');
      return;
    }

    if (myNewPassword.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }

    setIsChangingPass(true);
    try {
      const res = await authenticatedFetch(`${API_URL}v2/auth/change-password`, {
        method: 'POST',
        body: JSON.stringify({
          currentPassword,
          newPassword: myNewPassword
        })
      });

      const data = await res.json();
      if (res.ok) {
        setSuccessMsg('Contraseña actualizada correctamente.');
        setCurrentPassword('');
        setMyNewPassword('');
      } else {
        setError(data.message || 'Error al cambiar la contraseña.');
      }
    } catch (err) {
      setError('Error de conexión.');
    } finally {
      setIsChangingPass(false);
    }
  };

  const handleDeleteOperator = async (id, username) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar al operador "${username}"?`)) return;

    try {
      const res = await authenticatedFetch(`${API_URL}v2/auth/operators/${id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setSuccessMsg(`Operador "${username}" eliminado.`);
        fetchOperators();
      } else {
        const data = await res.json();
        setError(data.message || 'Error al eliminar operador.');
      }
    } catch (err) {
      setError('Error de conexión.');
    }
  };

  return (
    <div className="container py-4 text-light">
      <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
        <h2>Gestión de Seguridad u Operadores</h2>
        <a href="/admin/events-v2" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i> Volver a Eventos
        </a>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {successMsg && <div className="alert alert-success" role="alert">{successMsg}</div>}

      <div className="row g-4">
        {/* Cambiar mi contraseña */}
        <div className="col-md-5">
          <div className="card bg-dark border-secondary text-light h-100">
            <div className="card-header bg-secondary text-white fw-bold">
              Mi Perfil ({user?.username})
            </div>
            <div className="card-body">
              <h5 className="card-title text-info mb-3">Cambiar Mi Contraseña</h5>
              <form onSubmit={handleChangeOwnPassword}>
                <div className="mb-3">
                  <label className="form-label">Contraseña Actual</label>
                  <input
                    type="password"
                    className="form-control bg-dark text-light border-secondary"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Nueva Contraseña</label>
                  <input
                    type="password"
                    className="form-control bg-dark text-light border-secondary"
                    value={myNewPassword}
                    onChange={(e) => setMyNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <button type="submit" className="btn btn-warning w-100" disabled={isChangingPass}>
                  {isChangingPass ? 'Actualizando...' : 'Actualizar Mi Contraseña'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Registro y lista de operadores (Admin) */}
        {(user?.role === 'BusinessAdmin' || user?.role === 'SuperAdmin') && (
          <div className="col-md-7">
            <div className="card bg-dark border-secondary text-light mb-4">
              <div className="card-header bg-secondary text-white fw-bold">
                Registrar Nuevo Operador
              </div>
              <div className="card-body">
                <form onSubmit={handleCreateOperator} className="row g-3">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control bg-dark text-light border-secondary"
                      placeholder="Usuario"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <input
                      type="password"
                      className="form-control bg-dark text-light border-secondary"
                      placeholder="Contraseña (mín 6)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="col-md-4">
                    <select
                      className="form-select bg-dark text-light border-secondary"
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value)}
                    >
                      <option value="Operador">Operador</option>
                      <option value="BusinessAdmin">BusinessAdmin</option>
                    </select>
                  </div>
                  <div className="col-12 text-end">
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Guardando...' : 'Crear Operador'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="card bg-dark border-secondary text-light">
              <div className="card-header bg-secondary text-white fw-bold">
                Lista de Operadores Registrados
              </div>
              <div className="card-body p-0">
                {loading ? (
                  <div className="p-3 text-center">Cargando operadores...</div>
                ) : operators.length === 0 ? (
                  <div className="p-3 text-center text-muted">No hay operadores registrados.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-dark table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Usuario</th>
                          <th>Rol</th>
                          <th className="text-end">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {operators.map((op) => (
                          <tr key={op.id}>
                            <td>{op.username}</td>
                            <td>
                              <span className={`badge ${op.role === 'SuperAdmin' ? 'bg-danger' : op.role === 'BusinessAdmin' ? 'bg-warning text-dark' : 'bg-info text-dark'}`}>
                                {op.role}
                              </span>
                            </td>
                            <td className="text-end">
                              {op.id !== user?.id && (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDeleteOperator(op.id, op.username)}
                                  title="Eliminar Operador"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
